import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { decommafy, swal, toEnglishDigits, typeChecker } from "../../../utils/methods";
import { _post } from "../../../utils/network";

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb.component';
import FormField from "../../../components/Form/FormField/FormField.component";
import LoadingButton from '../../../components/LoadingButton/LoadingButton.component';
import ConfirmModal from "../../../components/Modals/ConfirmModal/ConfirmModal.component";

const TransactionsCreate = ({ componentType='create' }) => {
	const { t } = useTranslation();
	const { id } = useParams();
	const breadcrumbItems = [
		{ title: t('dashboard'), link: '/dashboard' },
		{ title: t('transactions'), link: '/transactions-list' },
		{ title: t(componentType) }
	];
	const confirmSwal = statements => {
		swal(() => statements(), {
			text: t('are_you_sure_to_do_this'),
			confirmButtonText: t('yes'),
			cancelButtonText: t('no')
		});
	};

	const [transactionCategories, setTransactionCategories] = useState([]);
	const getTransactionCategories = async () => {
		let body = {
			transactioncategory_name: null,
			page: 1,
			count: 1000000
		};
		let response = await _post('/transactioncategories/admin/getAll', body);
		switch(response.code){
			case 200:
				let tempTransactionCategories = [];
				response.data.transactioncategories.forEach(transactionCategory => {
					if(!transactionCategory.transactioncategory_information.no_edit)
						tempTransactionCategories.push({
							key: transactionCategory.transactioncategory_name,
							value: transactionCategory.transactioncategory_id
						});
				});
				setTransactionCategories(tempTransactionCategories);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};

	const [transactionUserTypedName, setTransactionUserTypedName] = useState(null);
	const transactionUserOnKeyUpHandler = () => {
		let typedValue = document.querySelector('#transactionUser input').value.trim();
		setTransactionUserTypedName(typedValue);
	};
	const [transactionUserTypedID, setTransactionUserTypedID] = useState(null);
	const transactionUserOnChangeHandler = event => {
		setTransactionUserTypedID(event.value);
	};

	const [transactionUsers, setTransactionUsers] = useState([]);
	const getTransactionUsers = async () => {
		let body = {
			user_fullname: transactionUserTypedName,
			phone_number: null,
			national_code: null,
			trade_code: null,
			inviter_code: null,
			is_credit: null,
			is_active: null,
			page: 1,
			count: 10
		};
		let response = await _post('/users/admin/getAll', body);
		switch(response.code){
			case 200:
				setTransactionUsers(response.data.users.map(user => {
					return {
						label: user.user_fullname + ' - ' + user.national_code,
						value: user.user_id
					};
				}));
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};

	const [createButtonIsLoading, setCreateButtonIsLoading] = useState(false);
	const createTransaction = event => {
		event.preventDefault();
		if(createButtonIsLoading)
			return 0;
		let categoryID = event.target.transactionCategory.value ? event.target.transactionCategory.value : null;
		let type = event.target.transactionType.value;
		let amount = parseInt(toEnglishDigits(decommafy(event.target.transactionAmount.value.trim())));
		if(!typeChecker(amount, 'number'))
			return toast.warn(t('enter_amount_code_correctly'));
		let sign = event.target.transactionSign.value;
		amount = sign === '1' ? amount : -1 * amount;
		let userId = transactionUserTypedID;
		if(!typeChecker(userId, 'uuid'))
			return toast.warn(t('select_user_correctly'));
		let description = event.target.transactionDescription.value.trim();
		if(!typeChecker(description, 'title'))
			return toast.warn(t('description') + ' ' + t('must_have_at_least_3_characters'));
		let body = {
			transactioncategory_id: categoryID,
			transaction_type: type,
			transaction_amount: amount,
			user_id: userId,
			transaction_description: description
		};
		confirmSwal(async () => {
			setCreateButtonIsLoading(true);
			let response = await _post('/transactions/admin/' + componentType, body);
			setCreateButtonIsLoading(false);
			switch(response.code){
				case 200:
					toast.success(response[t('api_message')]);
					if(componentType === 'create')
						event.target.reset();
					break;
				default:
					toast.error(response[t('api_message')]);
			}
		});
	};

	const [transaction, setTransaction] = useState({});
	const getTransaction = async () => {
		let body = {
			transaction_id: id
		};
		let response = await _post('/transactions/admin/getOne', body);
		switch(response.code){
			case 200:
				setTransaction(response.data.transaction);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};
	
	const transactionSigns = [
		{ key: t('positive'), value: 1 },
		{ key: t('negative'), value: 2 }
	];
	
	const transactionTypes = [
		{ key: t('score1_name'), value: 0 },
		{ key: t('score2_name'), value: 1 }
	];

	useEffect(() => {
		const fetchData = async () => {
			await getTransactionUsers();
		}
		fetchData();
	}, [transactionUserTypedName]);

	useEffect(() => {
		const fetchData = async () => {
			await getTransactionCategories();
			if(componentType === 'edit')
				getTransaction();
		}
		fetchData();
	}, []);

	return <>
			<Breadcrumb items={breadcrumbItems} />
		<div className="card mb-3">
			<div className="card-header">
				<h5 className="mb-0">{t(componentType)} {t('transaction')}</h5>
			</div>
			<div className="card-body bg-light">
				<form onSubmit={createTransaction}>
					<div className="row gx-2">
						<FormField type="select" fieldKey="transactionCategory" title={t('category')} selectItems={transactionCategories} rtl required />
						<FormField type="select" fieldKey="transactionType" title={t('type')} selectItems={transactionTypes} required />
						<FormField type="number" fieldKey="transactionAmount" title={t('amount')} required ltr />
						<FormField type="select" fieldKey="transactionSign" title={t('sign')} selectItems={transactionSigns} required />
						<FormField type="fullselect" fieldKey="transactionUser" title={t('user')} selectItems={transactionUsers} rtl required fullselectOnChange={transactionUserOnChangeHandler} fullselectOnKeyUp={transactionUserOnKeyUpHandler} />
						<FormField type="text" fieldKey="transactionDescription" title={t('description')} rtl required />
					</div>
					<div>
						{componentType === 'create' ?
							<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="create" isLoading={createButtonIsLoading}>
								<i className="fa fa-save"></i> {t('create')}
							</LoadingButton>
						:
							<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="edit" isLoading={createButtonIsLoading}>
								<i className="fa fa-save"></i> {t('update')}
							</LoadingButton>
						}
					</div>
				</form>
			</div>
		</div>
        <ConfirmModal action={createTransaction} />
	</>;
};

export default TransactionsCreate;
